import React from "react";
import { useTranslation } from "react-i18next";
import { countryDisplayName } from "../../Lit/acc-mixins/l10n.js";
import defaultContent from "../../../content/hat";
import {
  getPerformanceYearShort,
  getPeriodToDate,
  getSelectedPerformanceYearData,
  nextNPeriods,
} from "../../../common/util/period";
import { capitalizeName } from "../../../common/util/format";
import {
  MonthlyTotal,
  QLegsData,
  Qualification,
} from "../../../services/HigherAwardTracking/HigherAwardTrackingAPI.types";
import { storedLocale } from "../../Authentication/util";
import LeafDateTime from "../../../common/leaf/LeafDateTime/LeafDateTime";
import { getDisplayName } from "../../../common/util/displayName";
import "./LegsTrackingTable.scss";
import { useAppSelector } from "../../../store";
import { AFF_COUNTRY_CODE_MAP } from "../../../common/constants/aff-market-map";

type Props = {
  QLegList: QLegsData;
  currentProcessingPeriod: string;
  isInMarket: boolean;
  isInternational: boolean;
  isNonQualifiedInMarket: boolean;
  performanceYear?: string;
};

type RowType = "qualification" | "total";

const getCellClass = (
  base: string,
  options?: {
    isMonth?: boolean;
    yearClass?: "currentYear" | "previousYear";
    isHighlighted?: boolean;
  },
) => {
  return [
    base,
    "LegsTrackingTable__body-cell",
    options?.isMonth && "LegsTrackingTable__monthColumn",
    options?.yearClass && `LegsTrackingTable__${options.yearClass}`,
    options?.isHighlighted && "LegsTrackingTable__highlighted-column",
    options?.isHighlighted && options?.yearClass && "LegsTrackingTable__highlighted-" + options?.yearClass,
  ]
    .filter(Boolean)
    .join(" ");
};

const renderRowCells = (
  rowType: RowType,
  performancePeriods: string[],
  data: Qualification[] | MonthlyTotal[],
  yearClass: "currentYear" | "previousYear",
  highlightColumnIndex: number,
  currentProcessingPeriod: string,
  totalValue?: number,
  isNonQualifiedInMarket?: boolean,
) => {
  return (
    <>
      {performancePeriods.map((period, index) => {
        let item: Qualification | MonthlyTotal | undefined;

        if (rowType === "qualification") {
          item = (data as Qualification[]).find((q) => q.Period.toString() === period);
        } else {
          item = (data as MonthlyTotal[]).find((q) => q.Period.toString() === period);
        }

        let displayValue = "--";
        let percent = false;

        if (rowType === "qualification" && item && "QualCode" in item && item.QualCode) {
          const code = parseInt(item.QualCode, 10);
          displayValue = item.QualCode;
          percent = !Number.isNaN(code);
        } else if (rowType === "total" && item && "Count" in item) {
          displayValue = item.Count.toString();
        }

        return (
          <td
            key={period}
            className={getCellClass("", {
              isMonth: true,
              yearClass,
              isHighlighted: index === highlightColumnIndex,
            })}
          >
            {displayValue}
            {percent && <sup>%</sup>}
          </td>
        );
      })}
      <td className={getCellClass("", { isMonth: true, yearClass })}>
        {isNonQualifiedInMarket ? null : rowType === "qualification" && totalValue ? totalValue : null}
      </td>
    </>
  );
};

const LegsTrackingTable = ({
  QLegList,
  currentProcessingPeriod,
  isInMarket,
  isInternational,
  isNonQualifiedInMarket,
  performanceYear,
}: Props) => {
  const { t } = useTranslation(["hat", "common"]);
  const { selectedPeriod: reduxSelectedPeriod } = useAppSelector((state) => state.period);
  const selectedPeriod = performanceYear || reduxSelectedPeriod;

  if (currentProcessingPeriod === "0") {
    return <></>;
  }

  const { selectedPerformanceYear: previousPerformanceYear } = getSelectedPerformanceYearData(selectedPeriod, 1);
  const { selectedPerformanceYear: currentPerformanceYear } = getSelectedPerformanceYearData(selectedPeriod);

  const locale = storedLocale();

  const previousPY = previousPerformanceYear.slice(2, 4);
  const currentPY = currentPerformanceYear.slice(2, 4);

  const firstPerformancePeriod = `20${parseInt(getPerformanceYearShort(selectedPeriod)) - 1}09`;
  const previousPerformancePeriod = `20${parseInt(getPerformanceYearShort(previousPerformanceYear)) - 1}09`;

  const performancePeriods = nextNPeriods(firstPerformancePeriod, 12, true);
  const previousPerformancePeriods = nextNPeriods(previousPerformancePeriod, 12, true);

  const highlightColumnIndex = performancePeriods.findIndex((p) => p === currentProcessingPeriod);

  const getFormattedMonth = (period: string) => {
    const dateFromPeriod = getPeriodToDate(period).toISOString();
    return <LeafDateTime date={dateFromPeriod} options={{ month: "short" }} ignoreTimeZone={false} />;
  };

  const getTotalLabel = () => {
    if (isInMarket) {
      return t("legTrackingView.inMarketTotals", defaultContent.legTrackingView.inMarketTotals).toString();
    }
    if (isInternational) {
      return t("legTrackingView.internationalTotals", defaultContent.legTrackingView.internationalTotals).toString();
    }
    if (isNonQualifiedInMarket) {
      return t(
        "viewLegsView.nonQualifiedInMarketTotals",
        defaultContent.viewLegsView.nonQualifiedInMarketTotals,
      ).toString();
    }
    return "";
  };

  return (
    <div>
      <table cellSpacing="0" cellPadding="0" className="LegsTrackingTable">
        <thead>
          <tr className="LegsTrackingTable__row">
            <th className="LegsTrackingTable__header-cell LegsTrackingTable__aboName">
              {`${t("legTrackingView.nameNuberMarket", defaultContent.legTrackingView.nameNuberMarket)}`}
            </th>
            <th className="LegsTrackingTable__header-cell LegsTrackingTable__yearColumn">
              {`${t("legTrackingView.year", defaultContent.legTrackingView.year)}`}
            </th>
            {performancePeriods.map((month, index) => (
              <th
                key={month}
                className={getCellClass("LegsTrackingTable__header-cell", {
                  isMonth: true,
                  isHighlighted: index === highlightColumnIndex,
                })}
              >
                {getFormattedMonth(month)}
              </th>
            ))}
            <th className="LegsTrackingTable__header-cell LegsTrackingTable__monthColumn">
              {isNonQualifiedInMarket
                ? ""
                : t("legTrackingView.total", defaultContent.legTrackingView.total).toString()}
            </th>
          </tr>
        </thead>
        <tbody>
          {QLegList.Legs &&
            QLegList.Legs.map((leg, index) => {
              const countryCodNum = leg.IsoCountryCode ? Number.parseInt(leg.IsoCountryCode) : "";
              const countryCode = leg.IsoCountryCode
                ? Number.isNaN(countryCodNum)
                  ? leg.IsoCountryCode
                  : AFF_COUNTRY_CODE_MAP[leg.IsoCountryCode]
                : "";

              const aboDisplayName = capitalizeName(getDisplayName(leg.DisplayName, t));

              return (
                <React.Fragment key={`row-${index}`}>
                  <tr className="LegsTrackingTable__row" key={`${leg.Abo}-current`}>
                    <td rowSpan={2} className="LegsTrackingTable__body-cell LegsTrackingTable__aboName">
                      <div className="LegsTrackingTable__aboNameWrapper">
                        <div className="LegsTrackingTable__aboText">
                          <div>{aboDisplayName}</div>
                          <div className="LegsTrackingTable__aboNumber">
                            {leg.Abo} {countryCode ? countryDisplayName(locale, countryCode) : ""}
                          </div>
                        </div>
                        {leg.Number2Flag && (
                          <div className="LegsTrackingTable__tags">
                            <span className="LegsTrackingTable__tagBadge">#2</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className={getCellClass("", { yearClass: "currentYear" })}>
                      {`${t("legTrackingView.py", defaultContent.legTrackingView.py, { performanceYear: currentPY })}`}
                    </td>
                    {renderRowCells(
                      "qualification",
                      performancePeriods,
                      leg.SelectedPYQualifications.Qualification || [],
                      "currentYear",
                      highlightColumnIndex,
                      currentProcessingPeriod,
                      leg.SelectedPYQualifications.QualifiedMonthsYearToDate,
                      isNonQualifiedInMarket,
                    )}
                  </tr>
                  <tr className="LegsTrackingTable__row" key={`${leg.Abo}-previous`}>
                    <td className={getCellClass("", { yearClass: "previousYear" })}>
                      {`${t("legTrackingView.py", defaultContent.legTrackingView.py, { performanceYear: previousPY })}`}
                    </td>
                    {renderRowCells(
                      "qualification",
                      previousPerformancePeriods,
                      leg.PreviousPYQualifications.Qualification || [],
                      "previousYear",
                      highlightColumnIndex,
                      currentProcessingPeriod,
                      leg.PreviousPYQualifications.QualifiedMonthsYearToDate,
                      isNonQualifiedInMarket,
                    )}
                  </tr>
                </React.Fragment>
              );
            })}
          <tr className="LegsTrackingTable__row">
            <td rowSpan={2} className="LegsTrackingTable__body-cell LegsTrackingTable__aboName">
              {getTotalLabel()}
            </td>
            <td className={getCellClass("", { yearClass: "currentYear" })}>
              {`${t("legTrackingView.py", defaultContent.legTrackingView.py, { performanceYear: currentPY })}`}
            </td>
            {renderRowCells(
              "total",
              performancePeriods,
              QLegList.SelectedPYMonthlyTotals || [],
              "currentYear",
              highlightColumnIndex,
              currentProcessingPeriod,
            )}
          </tr>
          <tr className="LegsTrackingTable__row">
            <td className={getCellClass("", { yearClass: "previousYear" })}>
              {`${t("legTrackingView.py", defaultContent.legTrackingView.py, { performanceYear: previousPY })}`}
            </td>
            {renderRowCells(
              "total",
              previousPerformancePeriods,
              QLegList.PreviousPYMonthlyTotals || [],
              "previousYear",
              highlightColumnIndex,
              currentProcessingPeriod,
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default LegsTrackingTable;
